import {
    BacktestSpec as BacktestSpecSchema,
    BacktestJob as BacktestJobSchema,
    BacktestJobRecord,
    BacktestJobsSummary as BacktestJobsSummarySchema,
    BacktestUniverseSize as BacktestUniverseSizeSchema,
    BacktestComplexityEstimate as BacktestComplexityEstimateSchema,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import type {
    BacktestSpec,
    BacktestSpecPatch,
    NewBacktestSpec,
    BacktestJob,
    BacktestExecuteRequest,
    BacktestJobsSummary,
    BacktestUniverseSize,
    BacktestUniverseSizeRequest,
    BacktestComplexityEstimate,
    BacktestComplexityEstimateRequest,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import { createInstance } from 'src/api';

// Use a non-legacy instance so dates remain as strings for Zod to transform.
// The default `api` uses a legacy reviver that converts date strings to LocalDate/Instant
// objects before Zod parses, causing ZodISODate validation to reject them.
const api = createInstance(undefined, { useLegacySerialization: false });

/**
 * Query backtest specs with optional OData-style params.
 */
export async function getBacktestSpecs(params?: Record<string, string>): Promise<BacktestSpec[]> {
    const response = await api.get<BacktestSpec[]>('/backtests/specs/query', {
        params,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
        return [];
    }

    const items = Array.isArray(response.data) ? response.data : (response.data as { results: unknown[] }).results;
    return items.map((item: unknown) => BacktestSpecSchema.parse(item));
}

/**
 * Get a single backtest spec by ID.
 */
export async function getBacktestSpecById(id: string): Promise<BacktestSpec | null> {
    const response = await api.get<BacktestSpec>(`/backtests/specs/${encodeURIComponent(id)}`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
        return null;
    }

    return BacktestSpecSchema.parse(response.data);
}

/**
 * Create a new backtest spec.
 */
export async function createBacktestSpec(spec: NewBacktestSpec): Promise<BacktestSpec> {
    const response = await api.post<BacktestSpec>('/backtests/specs', spec);
    return BacktestSpecSchema.parse(response.data);
}

/**
 * Update an existing backtest spec (partial update).
 */
export async function updateBacktestSpec(id: string, patch: BacktestSpecPatch): Promise<BacktestSpec> {
    const response = await api.patch<BacktestSpec>(`/backtests/specs/${encodeURIComponent(id)}`, patch);
    return BacktestSpecSchema.parse(response.data);
}

/**
 * Delete a backtest spec.
 */
export async function deleteBacktestSpec(id: string): Promise<void> {
    await api.delete(`/backtests/specs/${encodeURIComponent(id)}`);
}

/**
 * Execute a backtest. Can pass either a saved spec ID or an inline spec.
 * Returns the created job.
 */
export async function executeBacktest(request: BacktestExecuteRequest): Promise<BacktestJob> {
    const response = await api.post<BacktestJob>('/backtests/execute', request);
    return BacktestJobSchema.parse(response.data);
}

/**
 * Query backtest jobs with optional OData-style params. Returns rows alongside
 * the server-reported total count when the request includes `$count=true`;
 * otherwise count falls back to the returned row count (caller should not rely
 * on it for pagination math without `$count`).
 */
export async function getBacktestJobs(
    params?: Record<string, string>,
): Promise<{ results: BacktestJobRecord[]; count: number }> {
    const response = await api.get<BacktestJobRecord[] | { results: BacktestJobRecord[]; count: number }>(
        '/backtests/jobs/query',
        {
            params,
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        },
    );

    if (response.status === 404) {
        return { results: [], count: 0 };
    }

    const { items, count } = Array.isArray(response.data)
        ? { items: response.data, count: response.data.length }
        : { items: response.data.results, count: response.data.count };
    const results = items.map((item: unknown) => BacktestJobRecord.parse(item));
    return { results, count };
}

/**
 * Status counts and best/worst PnL across the user's whole visible jobs set.
 * Independent of pagination — always reflects the full set, not the current page.
 */
export async function getBacktestJobsSummary(): Promise<BacktestJobsSummary> {
    const response = await api.get<BacktestJobsSummary>('/backtests/jobs/summary');
    return BacktestJobsSummarySchema.parse(response.data);
}

/**
 * Get a single backtest job by ID.
 */
export async function getBacktestJobById(id: string): Promise<BacktestJob | null> {
    const response = await api.get<BacktestJob>(`/backtests/jobs/${encodeURIComponent(id)}`, {
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
        return null;
    }

    return BacktestJobSchema.parse(response.data);
}

/**
 * Delete a backtest job. Server returns 204 on success.
 */
export async function deleteBacktestJob(id: string): Promise<void> {
    await api.delete(`/backtests/jobs/${encodeURIComponent(id)}`);
}

/**
 * Cancel a running backtest job. Returns the updated record (status will be
 * `'cancelled'` on a successful cancel; if the engine reports the job was
 * already terminal the response passes through unchanged).
 */
export async function cancelBacktestJob(id: string): Promise<BacktestJobRecord> {
    const response = await api.patch<BacktestJobRecord>(`/backtests/jobs/${encodeURIComponent(id)}/cancel`);
    return BacktestJobRecord.parse(response.data);
}

/**
 * Preview how many securities a backtest universe will match. Accepts either a
 * saved spec id or an inline `{ universePlan, startDate, endDate }`.
 */
export async function getBacktestUniverseSize(request: BacktestUniverseSizeRequest): Promise<BacktestUniverseSize> {
    const response = await api.post<BacktestUniverseSize>('/backtests/universe-size', request);
    return BacktestUniverseSizeSchema.parse(response.data);
}

/**
 * Estimate the cost/complexity of running a backtest. Accepts either a saved
 * `specId` or an inline `{ spec }`. Returns a tier classification, score, and
 * the universe size that fed the estimate.
 */
export async function getBacktestComplexityEstimate(
    request: BacktestComplexityEstimateRequest,
): Promise<BacktestComplexityEstimate> {
    const response = await api.post<BacktestComplexityEstimate>('/backtests/complexity-estimate', request);
    return BacktestComplexityEstimateSchema.parse(response.data);
}
